import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProgressStepper, { RESPONDENT_STEPS } from "../components/ProgressStepper";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Mic, Square, CheckCircle2, ArrowRight, Timer, RotateCcw, Loader2 } from "lucide-react";

const PROMPT_TEXT = "Tell us how you are doing, your workplace, and what you would like to be.";
const RECORD_SECONDS = 30;

// One-time 30-second voice acquisition. Fired between /voice (sustained
// phonation) and /questionnaires so we capture a natural free-speech sample
// answering three prompts. Skipped forever once the respondent submits one.
export default function VoiceIntro() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | recording | ready | uploading | done
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [checking, setChecking] = useState(true);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    // Fast-forward respondents who already captured an intro sample.
    api.get("/voice-intro/status")
      .then((r) => {
        if (r.data?.completed) { nav("/questionnaires", { replace: true }); return; }
        setChecking(false);
      })
      .catch(() => setChecking(false));
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav]);

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mimeType });
        setBlob(b);
        setAudioUrl(URL.createObjectURL(b));
        setStatus("ready");
      };
      recorderRef.current = rec;
      rec.start();
      setElapsed(0);
      setStatus("recording");
      // 30-second countdown — stops the recorder when it hits the cap.
      tickRef.current = setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          if (next >= RECORD_SECONDS) stopRecording();
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error("Microphone access denied — please enable it and retry.");
    }
  };

  const retake = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null); setBlob(null); setElapsed(0); setStatus("idle");
  };

  const submit = async () => {
    if (!blob) return;
    setStatus("uploading");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "intro.webm");
      fd.append("duration_seconds", String(elapsed));
      await api.post("/voice-intro/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Voice sample saved — heading to your questionnaires");
      setStatus("done");
      setTimeout(() => nav("/questionnaires", { replace: true }), 700);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please retry");
      setStatus("ready");
    }
  };

  const pct = Math.min(100, (elapsed / RECORD_SECONDS) * 100);
  const remaining = Math.max(0, RECORD_SECONDS - elapsed);

  if (checking) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container-narrow py-20 text-muted-foreground">Checking voice status…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <ProgressStepper steps={RESPONDENT_STEPS} current="voice" />

        <div className="mb-8 mt-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs" data-testid="voice-intro-badge">
            <Mic size={12} /> Voice acquisition · 30 seconds
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-4"
              data-testid="voice-intro-title">
            One quick voice sample before your questionnaires
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            This helps us understand how you speak in your own words — no right or wrong answer.
            You'll get a single 30-second window to answer the prompt below.
          </p>
        </div>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-10 space-y-6">
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-5" data-testid="voice-intro-prompt">
              <div className="text-[10px] uppercase tracking-wider text-primary mb-2">Prompt</div>
              <p className="font-display text-xl sm:text-2xl leading-snug">{PROMPT_TEXT}</p>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Timer size={12} /> {remaining}s left</span>
              <span>{status === "recording" ? "Recording…" : status === "ready" ? "Preview below" : "Ready when you are"}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" data-testid="voice-intro-progress">
              <div className={"h-1.5 rounded-full transition-all " +
                              (status === "recording" ? "bg-destructive" : "bg-primary")}
                   style={{ width: `${pct}%` }} />
            </div>

            {audioUrl && (
              <audio src={audioUrl} controls className="w-full" data-testid="voice-intro-audio" />
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              {status === "idle" && (
                <Button onClick={startRecording} size="lg"
                        className="rounded-full px-8 mano-lift"
                        data-testid="voice-intro-start-btn">
                  <Mic size={16} /> Start recording
                </Button>
              )}
              {status === "recording" && (
                <Button onClick={stopRecording} variant="destructive" size="lg"
                        className="rounded-full px-8"
                        data-testid="voice-intro-stop-btn">
                  <Square size={16} /> Stop
                </Button>
              )}
              {status === "ready" && (
                <>
                  <Button onClick={submit} size="lg" className="rounded-full px-8 mano-lift"
                          data-testid="voice-intro-submit-btn">
                    <CheckCircle2 size={16} /> Use this recording <ArrowRight size={14} />
                  </Button>
                  <Button onClick={retake} variant="outline" size="lg"
                          className="rounded-full px-6"
                          data-testid="voice-intro-retake-btn">
                    <RotateCcw size={14} /> Retake
                  </Button>
                </>
              )}
              {status === "uploading" && (
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground"
                     data-testid="voice-intro-uploading">
                  <Loader2 size={14} className="animate-spin" /> Saving…
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground pt-3 border-t border-border/40">
              We store the audio securely, gated to your case's clinicians and admins.
              You can withdraw consent from your results page at any time.
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
